/**
 * COMMERCIAL CHANGE ORDER BUILDER
 *
 * This component handles the "commercial" aspect of scope creep:
 * Negotiating, pricing, and generating documents for client signature.
 *
 * NOT to be confused with the low-level "audit" change tracking system.
 */

import React, { useState, useEffect } from 'react';
import { FileText, Download, Save, ArrowLeft, Check, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

// Duplicate interface for frontend safety
interface ScopeDelta {
  deltaId: string;
  description: string;
  deltaType: "addition" | "expansion" | "complexity";
  estimatedImpact: "small" | "medium" | "large";
  configItemName?: string;
}

export interface ChangeOrderDraft {
  summary: string;
  scopeAdditions: string[];
  clientOptions: {
    removeScope?: string[];
    clientOwnedWork?: string[];
    additionalBudget?: {
      estimate: string;
      rationale: string;
    };
  };
}

interface ChangeOrderBuilderProps {
  selectedDeltas: ScopeDelta[];
  companyId: string;
  configItemId: string;
  userId: string;
  userName: string;
  onBack: () => void;
  onSave?: (draft: ChangeOrderDraft) => void;
  initialDraft?: ChangeOrderDraft;
  existingChangeOrderId?: string;
  isMultiItemMode?: boolean;
}

export function ChangeOrderBuilder({ selectedDeltas, companyId, configItemId, userId, userName, onBack, onSave, initialDraft, existingChangeOrderId, isMultiItemMode }: ChangeOrderBuilderProps) {
  const [draft, setDraft] = useState<ChangeOrderDraft>(initialDraft || {
    summary: '',
    scopeAdditions: [],
    clientOptions: {
      removeScope: [],
      clientOwnedWork: [],
      additionalBudget: { estimate: '', rationale: '' }
    }
  });

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate initial draft on mount ONLY if no initialDraft provided
  useEffect(() => {
    if (!initialDraft) {
      generateInitialDraft();
    }
  }, [selectedDeltas]);

  const generateInitialDraft = () => {
    // 1. Summary
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const summary = `During delivery discovery, the following items were identified as outside the scope of the signed SOW dated ${dateStr}.`;

    // 2. Additions
    const additions = selectedDeltas.map(d => {
       const desc = d.description.replace(/New asset type discovered: ([^.]+)/, "Inclusion of $1 configuration");
       const prefix = d.configItemName ? `[${d.configItemName}] ` : '';
       return `${prefix}Add: ${desc} (Impact: ${d.estimatedImpact})`;
    });

    // 3. Options
    const removeScope = [
        "Swap scope: remove a comparable item from the current scope."
    ];

    const clientOwnedWork = [
        "Client-owned: your team completes specific tasks to reduce consultant effort."
    ];

    // Additional Budget Option
    const changeOrderOption = "Change order: increase budget to cover additional consultant effort.";

    let hours = 0;
    selectedDeltas.forEach(d => {
        if (d.estimatedImpact === 'large') hours += 8;
        else if (d.estimatedImpact === 'medium') hours += 4;
        else hours += 1;
    });

    setDraft({
        summary,
        scopeAdditions: additions,
        clientOptions: {
            removeScope,
            clientOwnedWork,
            additionalBudget: {
                estimate: `${hours} hours`,
                rationale: changeOrderOption
            }
        }
    });
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/change-orders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token') || ''}`,
            },
            body: JSON.stringify({
                draft,
                userId,
                userName,
                changeOrderId: existingChangeOrderId
            })
        });

        if (!response.ok) {
            throw new Error('Failed to generate change order');
        }

        const data = await response.json();
        
        // Helper to download base64
        const downloadBase64 = (base64: string, filename: string, type: string) => {
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type });
            
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };

        // Trigger downloads
        downloadBase64(data.pdfBase64, `${data.ref}.pdf`, 'application/pdf');
        downloadBase64(data.docxBase64, `${data.ref}.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
        
        toast.success("Change Order generated and saved");
        if (onSave) onSave(draft);
        
    } catch (e) {
        console.error(e);
        toast.error("Failed to generate change order");
    } finally {
        setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Review
          </Button>
          <h2 className="text-xl font-bold">Change Order Draft Builder</h2>
        </div>
        <div className="flex gap-2">
           <Button variant="outline" onClick={() => setIsPreviewMode(!isPreviewMode)}>
             {isPreviewMode ? "Edit Mode" : "Preview Mode"}
           </Button>
           <Button onClick={handleDownload} disabled={isGenerating}>
             <Download className="w-4 h-4 mr-2" />
             {isGenerating ? "Generating..." : "Export PDF & DOCX"}
           </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Editor Pane */}
        {!isPreviewMode && (
        <Card className="no-print">
            <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                    <Label>Executive Summary</Label>
                    <Textarea 
                        value={draft.summary} 
                        onChange={e => setDraft({...draft, summary: e.target.value})}
                        className="h-24" 
                    />
                </div>

                <div className="space-y-2">
                    <Label>Scope Variance (The "What")</Label>
                    {draft.scopeAdditions.map((item, idx) => (
                        <div key={idx} className="flex gap-2">
                            <Input 
                                value={item} 
                                onChange={e => {
                                    const newItems = [...draft.scopeAdditions];
                                    newItems[idx] = e.target.value;
                                    setDraft({...draft, scopeAdditions: newItems});
                                }}
                            />
                            <Button variant="ghost" size="icon" onClick={() => {
                                const newItems = draft.scopeAdditions.filter((_, i) => i !== idx);
                                setDraft({...draft, scopeAdditions: newItems});
                            }}>
                                <span className="text-red-500">×</span>
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => {
                        setDraft({...draft, scopeAdditions: [...draft.scopeAdditions, "New scope item..."]});
                    }}>
                        + Add Item
                    </Button>
                </div>

                <Separator />

                <div className="space-y-4">
                    <Label className="text-lg font-semibold">Resolution Options</Label>
                    
                    <div className="space-y-2">
                        <Label>Option A: Additional Budget</Label>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <span className="text-xs text-muted-foreground">Estimate</span>
                                <Input 
                                    value={draft.clientOptions.additionalBudget?.estimate}
                                    onChange={e => setDraft({
                                        ...draft, 
                                        clientOptions: {
                                            ...draft.clientOptions, 
                                            additionalBudget: {...draft.clientOptions.additionalBudget!, estimate: e.target.value}
                                        }
                                    })}
                                />
                            </div>
                            <div className="space-y-1">
                                <span className="text-xs text-muted-foreground">Rationale</span>
                                <Input 
                                    value={draft.clientOptions.additionalBudget?.rationale}
                                    onChange={e => setDraft({
                                        ...draft, 
                                        clientOptions: {
                                            ...draft.clientOptions, 
                                            additionalBudget: {...draft.clientOptions.additionalBudget!, rationale: e.target.value}
                                        }
                                    })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Option B: Client Responsibility</Label>
                         {draft.clientOptions.clientOwnedWork?.map((item, idx) => (
                             <Input key={idx} value={item} onChange={(e) => {
                                 const newItems = [...(draft.clientOptions.clientOwnedWork || [])];
                                 newItems[idx] = e.target.value;
                                 setDraft({...draft, clientOptions: {...draft.clientOptions, clientOwnedWork: newItems}});
                             }} />
                         ))}
                    </div>
                </div>
            </CardContent>
        </Card>
        )}

        {/* Document Preview Pane - Rendered as "Page" */}
        <div className={`print:w-full ${isPreviewMode ? 'w-full max-w-3xl mx-auto' : ''}`}>
            <div className="bg-white text-black p-12 shadow-lg min-h-[800px] border relative">
                {/* Branding Header */}
                <div className="flex justify-between items-start mb-12 border-b pb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Change Order</h1>
                        <p className="text-slate-500 mt-1">Scope Variance & Resolution</p>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                        <p>Date: {new Date().toLocaleDateString()}</p>
                        <p>Ref: CO-{new Date().getFullYear()}-001</p>
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    <section>
                        <h3 className="text-sm uppercase tracking-wider text-slate-500 font-semibold mb-2">Executive Summary</h3>
                        <p className="text-slate-800 leading-relaxed text-justify">
                            {draft.summary}
                        </p>
                    </section>

                    <section>
                        <h3 className="text-sm uppercase tracking-wider text-slate-500 font-semibold mb-3">Identified Scope Variance</h3>
                        <ul className="list-disc pl-5 space-y-2">
                            {draft.scopeAdditions.map((item, i) => (
                                <li key={i} className="text-slate-800">{item}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="mt-8 bg-slate-50 p-6 rounded-lg border border-slate-100">
                        <h3 className="text-sm uppercase tracking-wider text-slate-500 font-semibold mb-4">Resolution Options</h3>
                        
                        <div className="mb-6">
                            <h4 className="text-base font-bold text-slate-900 mb-1">Option 1: Scope Expansion (Recommended)</h4>
                            <p className="text-sm text-slate-600 mb-2">Incorporating identified items into the delivery plan.</p>
                            <div className="flex items-center justify-between bg-white p-3 border rounded">
                                <span className="font-medium">Estimated Impact</span>
                                <span className="font-bold text-slate-900">{draft.clientOptions.additionalBudget?.estimate}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-2 italic">{draft.clientOptions.additionalBudget?.rationale}</p>
                        </div>

                        <div>
                            <h4 className="text-base font-bold text-slate-900 mb-1">Option 2: Client Execution</h4>
                            <p className="text-sm text-slate-600 mb-2">Client team assumes responsibility for configuring identified items.</p>
                            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                                {draft.clientOptions.clientOwnedWork?.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    <div className="mt-16 pt-8 border-t flex justify-between gap-12">
                        <div className="w-1/2 border-t border-black pt-2">
                            <p className="font-bold text-slate-900">Consultant Signature</p>
                            <p className="text-xs text-slate-500">Authorized Representative</p>
                        </div>
                        <div className="w-1/2 border-t border-black pt-2">
                            <p className="font-bold text-slate-900">Client Signature</p>
                            <p className="text-xs text-slate-500">Acceptance of Resolution Option ___</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}
